import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Trash2, Star, RefreshCw } from "lucide-react";

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending"); // pending | approved | all
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
    } catch (e) { toast.error("Loading failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/testimonials/${id}/approve`); toast.success("Approved & published"); load(); }
    catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/testimonials/${id}/reject`); toast.success("Hidden from website"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const pendingCount = items.filter((i) => i.approved === false).length;
  const approvedCount = items.filter((i) => i.approved !== false).length;

  const filtered = items.filter((i) => {
    if (filter === "pending") return i.approved === false;
    if (filter === "approved") return i.approved !== false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--dd-green)]">Testimonials moderation</h1>
          <p className="text-sm text-[var(--dsf-text-secondary)] mt-1">
            Review customer submissions before they appear on the website.
          </p>
        </div>
        <button onClick={load} className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--dsf-border)] hover:bg-[var(--dd-bone)]" data-testid="testimonials-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { v: "pending", label: `Pending (${pendingCount})`, cls: "bg-amber-500" },
          { v: "approved", label: `Approved (${approvedCount})`, cls: "bg-[var(--dd-green)]" },
          { v: "all", label: `All (${items.length})`, cls: "bg-[var(--dd-coral)]" },
        ].map((t) => (
          <button
            key={t.v} onClick={() => setFilter(t.v)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition ${filter === t.v ? `${t.cls} text-white` : "bg-[var(--dd-bone)] text-[var(--dd-green)] hover:bg-[var(--dd-green-soft)]"}`}
            data-testid={`testimonials-tab-${t.v}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-12 text-center text-[var(--dsf-text-secondary)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[var(--dsf-border)] p-12 text-center" data-testid="testimonials-empty">
          <div className="text-3xl mb-2">📝</div>
          <div className="font-semibold text-[var(--dd-green)]">No testimonials here</div>
          <div className="text-sm text-[var(--dsf-text-secondary)] mt-1">
            {filter === "pending" ? "All submissions have been reviewed." : "Nothing to display."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-[var(--dsf-border)] p-5 flex flex-col" data-testid={`testimonial-card-${it.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-bold text-[var(--dd-green)]">{it.name}</div>
                  <div className="text-xs text-[var(--dsf-text-muted)]">{it.role || it.city || ""}</div>
                </div>
                <div className="flex">
                  {Array.from({ length: it.rating || 5 }).map((_, j) => (
                    <Star key={j} size={14} fill="currentColor" className="text-[var(--dd-mustard)]" />
                  ))}
                </div>
              </div>
              <p className="text-sm text-[var(--dsf-text-secondary)] leading-relaxed flex-1 italic">
                "{it.quote || it.quote_fr || it.text}"
              </p>
              <div className="text-[10px] text-[var(--dsf-text-muted)] uppercase tracking-wider mt-3">
                {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
              </div>
              <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-[var(--dsf-border)]">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${it.approved === false ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                  {it.approved === false ? "⏳ Pending" : "✓ Published"}
                </span>
                <div className="flex gap-1.5">
                  {it.approved === false ? (
                    <button onClick={() => approve(it.id)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[var(--dd-green)] text-white hover:opacity-90" data-testid={`approve-${it.id}`}>
                      <Check size={13} /> Approve
                    </button>
                  ) : (
                    <button onClick={() => reject(it.id)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-amber-400 text-amber-700 hover:bg-amber-50" data-testid={`hide-${it.id}`}>
                      <X size={13} /> Hide
                    </button>
                  )}
                  <button onClick={() => remove(it.id)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--dd-coral)] text-[var(--dd-coral)] hover:bg-red-50" data-testid={`delete-${it.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
